import React, { useState, useContext, useMemo, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Link } from 'react-router-dom';
import WithdrawOrDepositTemporaryFundV2 from './components/WithdrawOrDepositTemporaryFundV2/WithdrawOrDepositTemporaryFundV2';
import TemporaryFundsFiltersV2 from './components/TemporaryFundsFiltersV2/TemporaryFundsFiltersV2';
import { dollarFormat, getPropByString } from 'utils/utils';
import { useCaseListFilter as useFilter } from 'modules/cases/hooks/useCaseListFilter';
import { useTemporaryFundListV2Query } from './hooks/queries/useTemporaryFundListV2Query';
import ResizableDataTable from 'components/ResizableDataTable/ResizableDataTable';
import EllipsisPagination from 'components/common/EllipsisPagination/EllipsisPagination';
import { useQueryClient } from '@tanstack/react-query';
import { getPageLimit, pickExtractedDate, getFilePathForPagination } from './helpers/temporaryFundHelpersV2';
import { handlePageNumberInPagination } from 'common/pagination';
import constants from 'constants/index';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { UserDetailsContext } from 'context/userDetailsContext';
import { getTemporaryFundBalance } from 'services/Billing/temporaryFunds/temporaryFundServices';
import { useQuery } from '@tanstack/react-query';
import { depositOrWithdrawTemporaryFund, updateTemporaryFundTransaction } from 'services/Billing/temporaryFunds/temporaryFundServices';
import GlobalLoader from 'components/GlobalLoader/GlobalLoader';
import { queryKeys } from '../config/queryClient';

const TemporaryFundsV2 = (props) => {
  const { caseView, isLeadSpecific, caseDetails, getCaseDetails, isActiveTab = true } = props || {};
  const userContext = useContext(UserDetailsContext);
  const { userDetails } = useUserDetailsContext();
  const queryClient = useQueryClient();

  const trustAccountId = caseDetails?.default_trust_account?.length ? caseDetails?.default_trust_account?.[0]?.account_id : '';

  const { filter, filterOnChange, refreshList } = useFilter({
    case_id: caseView ? caseDetails?.case_id : null,
    account_id: trustAccountId,
  });

  // State management
  const [temporaryFundModalOptions, setTemporaryFundModalOptions] = useState({ show: false, isUpdate: false });
  const [pageLimit, setPageLimit] = useState(getPageLimit(isLeadSpecific, caseView, userContext) ?? 10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState(null); // No default sort - user can choose
  const [sortOrder, setSortOrder] = useState(null); // 1 for ascending, -1 for descending

  // Map DataTable field names to API sort_by field names
  // API supports: case_name, transaction_date, account_name, amount, credit_account_name, from_or_to_account
  const getCurrentSortField = useMemo(() => {
    if (!sortField) return ''; // No sort field selected
    const fieldMap = {
      case_name: 'case_name',
      transaction_date: 'transaction_date',
      record_created_date: 'transaction_date', // Map record_created_date to transaction_date for API
      account_name: 'credit_account_name', // Account column uses credit_account_name for sorting
      amount: 'amount',
      from_or_to_account: 'from_or_to_account', // Transferred From/To column uses from_or_to_account for sorting
    };
    return fieldMap[sortField] || '';
  }, [sortField]);

  const getCurrentSortOrder = useMemo(() => {
    if (sortOrder === null || sortOrder === undefined) return ''; // No sort order selected
    return sortOrder === 1 ? 'asc' : 'desc';
  }, [sortOrder]);

  // Compute query filters from filter state
  const queryFilters = useMemo(() => {
    const dateFilters = pickExtractedDate(filter?.dateFilter || [], userDetails?.timezone);
    return {
      case_id: filter?.case_id || caseDetails?.case_id || '',
      account_id: filter?.account_id || trustAccountId || '',
      transaction_date_lower: dateFilters.transaction_date_lower || '',
      transaction_date_upper: dateFilters.transaction_date_upper || '',
      sort_by: getCurrentSortField,
      sort_order: getCurrentSortOrder,
    };
  }, [filter, caseDetails, trustAccountId, userDetails?.timezone, getCurrentSortField, getCurrentSortOrder]);

  // React Query for temporary funds list
  const { temporaryFunds, totalRecords, isLoading } = useTemporaryFundListV2Query({
    limit: pageLimit,
    page: currentPage,
    ...queryFilters,
    enabled: isActiveTab, // Only fetch when tab is active
  });

  // React Query for temporary fund balance
  const { data: balanceData } = useQuery({
    queryKey: ['temporary-fund-balance', trustAccountId, caseDetails?.case_id],
    queryFn: () => getTemporaryFundBalance(trustAccountId, caseDetails?.case_id),
    enabled: !!(trustAccountId || caseDetails?.case_id),
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  const temporaryFundBalance = balanceData?.data?.temporary_fund_balance ? parseFloat(balanceData.data.temporary_fund_balance) : 0;

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPageLimit(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
    const filePath = getFilePathForPagination(isLeadSpecific, caseView);
    handlePageNumberInPagination(userContext, filePath.module, filePath.subModule, filePath.listName, newPageSize);
  };

  // Handle filter changes - reset to page 1
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  // Sorting handler
  const onSort = (event) => {
    const { sortField: field, sortOrder: order } = event;

    setSortField(field);
    setSortOrder(order);
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  // Deposit or withdraw temporary fund amount
  const depositOrWithdrawTemporaryFundAmount = async (params) => {
    const { hideModal, data } = params;
    try {
      await depositOrWithdrawTemporaryFund({ data, is_lead: isLeadSpecific ? true : false });
      hideModal();
      // Invalidate all temporary funds listV2 queries to ensure all pages/filters are updated
      // This automatically triggers refetch for all matching queries
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return key[0] === 'temporary-funds' && key[1] === 'list' && key[2] === 'v2';
        },
      });
      // Invalidate balance query
      queryClient.invalidateQueries({ queryKey: ['temporary-fund-balance'] });
      // Trust accounting list + balance (deposit/withdraw moves funds to/from trust)
      queryClient.invalidateQueries({ queryKey: queryKeys.trustAccounting.all });
      queryClient.invalidateQueries({ queryKey: ['trust-account-balance'] });
      if (typeof getCaseDetails === 'function') {
        getCaseDetails();
      }
    } catch (err) {
      console.log('Error:depositOrWithdrawTemporaryFund', err);
    }
  };

  // Update temporary fund transactions
  const updateTemporaryFundTransactions = async (params) => {
    const { hideModal, data } = params;
    try {
      await updateTemporaryFundTransaction({ data, is_lead: isLeadSpecific ? true : false });
      hideModal();
      // Invalidate all temporary funds listV2 queries to ensure all pages/filters are updated
      // This automatically triggers refetch for all matching queries
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return key[0] === 'temporary-funds' && key[1] === 'list' && key[2] === 'v2';
        },
      });
      // Invalidate balance query
      queryClient.invalidateQueries({ queryKey: ['temporary-fund-balance'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.trustAccounting.all });
      queryClient.invalidateQueries({ queryKey: ['trust-account-balance'] });
      if (typeof getCaseDetails === 'function') {
        getCaseDetails();
      }
    } catch (err) {
      console.log('Error:updateTemporaryFundTransaction', err);
    }
  };

  const amountBody = (rowData) => {
    const { entry_type } = rowData;
    return (
      <div>
        <span className="p-column-title text-break">From trust</span>
        <div>
          <span className={entry_type === 'cr' ? 'text-success' : 'text-danger'} title={dollarFormat(rowData?.amount)}>
            {dollarFormat(rowData?.amount)}
          </span>
          {entry_type === 'cr' && <i className="fas fa-arrow-down ms-2"></i>}
          {entry_type !== 'cr' && <i className="fas fa-arrow-up ms-2"></i>}
          <br></br>
          {rowData?.trust_info?.length > 0 &&
            rowData?.trust_info.map((index) => {
              return (
                <span key={index?.account_id}>
                  Account: {index.account_name}
                  <br></br>
                  Balance: {dollarFormat(index.trust_balance)}
                </span>
              );
            })}
        </div>
      </div>
    );
  };

  const fromOrToBody = (rowData) => {
    const { entry_type, from_or_to_account } = rowData || {};
    const displayValue = from_or_to_account || '-';
    return (
      <>
        <span className="p-column-title text-break">To/From account</span>
        <span className="text-muted F-size12">{entry_type === 'db' ? 'To' : 'From'}:</span>
        <span className="ms-2 primary-dark" title={displayValue}>
          {displayValue}
        </span>
      </>
    );
  };

  const dateBody = (rowData) => {
    const formattedDate = convertDateFromApiToUserTimezone(
      rowData?.record_created_date,
      userDetails?.timezone,
      constants.month_date_year_format
    );
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Date of Transaction</span>
        <span className="text-break" title={formattedDate !== '-' ? formattedDate : undefined}>
          {rowData?.record_created_date ? formattedDate : '-'}
        </span>
      </React.Fragment>
    );
  };

  const caseTemplate = (rowData, ...rest) => {
    const { header, field } = rest?.[0] || {};
    return (
      <React.Fragment>
        <span className="p-column-title text-break">{header}</span>
        <span className="text-break">
          <Link
            className="cursor-pointer text-decoration-none F-size14 call-back"
            to={`/${rowData?.is_lead ? 'leads' : 'cases'}/${rowData?.case_id}`}
            title={getPropByString(rowData, field)}
          >
            {getPropByString(rowData, field)}
          </Link>
        </span>
      </React.Fragment>
    );
  };

  const rowClassName = (rowData) => ({ strikethrough: rowData.is_strikethrough });

  const accountBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Account</span>
        <span className="text-break">
          <Link
            to="/settings/accounts"
            className="call-back text-decoration-none"
            title={rowData?.credit_account_name || rowData?.debit_account_name}
          >
            {rowData?.credit_account_name || rowData?.debit_account_name}
          </Link>
        </span>
      </React.Fragment>
    );
  };

  const refreshData = () => {
    refreshList({
      case_id: caseView ? caseDetails?.case_id : null,
    });
    // Don't call refetch() - let React Query use cache if available
    // React Query will automatically check cache first (within staleTime) before making network request
    // This allows cached data to be used when clearing filters
  };

  return (
    <>
      <GlobalLoader />
      {caseView && (
        <div className="d-flex justify-content-end align-items-center mb-3">
          <Button
            label="Deposit"
            className="p-button p-button-primary"
            onClick={() => {
              setTemporaryFundModalOptions({ show: true, entryType: 'cr', isUpdate: false });
            }}
          />
          <Button
            label="Withdraw"
            className="p-button p-button-primary ms-2"
            onClick={() => {
              setTemporaryFundModalOptions({ show: true, entryType: 'db', isUpdate: false });
            }}
            disabled={temporaryFundBalance === 0}
          />
        </div>
      )}
      <TemporaryFundsFiltersV2
        filter={filter}
        filterOnChange={filterOnChange}
        refreshData={refreshData}
        caseView={caseView}
        temporaryFundBalance={temporaryFundBalance}
      />

      <div className="datatable-responsive bg-white shadow-middle mb-3">
        <ResizableDataTable
          value={temporaryFunds || []}
          rowClassName={rowClassName}
          className="p-datatable-responsive"
          emptyMessage={isLoading ? 'Loading...' : 'No records found'}
          paginator={false}
          rows={pageLimit}
          storageKey="temporary_funds_column_widths"
          sortMode="server"
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={onSort}
          removableSort={false}
          columns={[
            {
              field: 'case_name',
              header: 'Case Name',
              body: caseTemplate,
              bodyClassName: 'ellipsis-text',
              hidden: caseView,
              sortable: true,
            },
            { field: 'record_created_date', header: 'Date of Transaction', body: dateBody, bodyClassName: 'ellipsis-text', sortable: true },
            {
              field: 'account_name',
              header: 'Account',
              body: accountBody,
              bodyClassName: 'ellipsis-text',
              hidden: caseView,
              sortable: true,
            },
            { field: 'amount', header: 'Amount', body: amountBody, bodyClassName: 'ellipsis-text', sortable: true },
            {
              field: 'from_or_to_account',
              header: 'Transferred From/To',
              body: fromOrToBody,
              bodyClassName: 'ellipsis-text',
              sortable: true,
            },
          ]}
        />
        <EllipsisPagination
          currentPage={currentPage}
          totalRecords={totalRecords}
          pageSize={pageLimit}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          showPageSizeDropdown={true}
        />
      </div>
      {temporaryFundModalOptions?.show && (
        <WithdrawOrDepositTemporaryFundV2
          onHide={() => setTemporaryFundModalOptions({ show: false })}
          caseDetails={caseDetails}
          temporaryFundModalOptions={temporaryFundModalOptions}
          depositOrWithdrawTemporaryFundAmount={depositOrWithdrawTemporaryFundAmount}
          updateTemporaryFundTransactions={updateTemporaryFundTransactions}
          temporaryFundBalance={temporaryFundBalance}
        />
      )}
    </>
  );
};

export default TemporaryFundsV2;
